import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "../components/ui/dialog";
import { 
  Plus, GraduationCap, Clock, Users, BookOpen, Award
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const categories = ["Técnico", "Liderazgo", "Comunicación", "Seguridad", "Compliance", "Onboarding"];

const Training = () => {
  const [trainings, setTrainings] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trainingDialogOpen, setTrainingDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedTraining, setSelectedTraining] = useState(null);
  
  const [trainingForm, setTrainingForm] = useState({
    title: "", description: "", duration_hours: "", instructor: "", category: ""
  });
  
  const [assignForm, setAssignForm] = useState({
    training_id: "", employee_id: "", due_date: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [trainingsRes, assignmentsRes, employeesRes] = await Promise.all([
        axios.get(`${API}/trainings`, { headers, withCredentials: true }),
        axios.get(`${API}/training-assignments`, { headers, withCredentials: true }),
        axios.get(`${API}/employees`, { headers, withCredentials: true })
      ]);
      setTrainings(trainingsRes.data);
      setAssignments(assignmentsRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTraining = async (e) => {
    e.preventDefault();
    if (!trainingForm.title || !trainingForm.duration_hours || !trainingForm.category) {
      toast.error("Completa los campos requeridos");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/trainings`, {
        ...trainingForm,
        duration_hours: parseInt(trainingForm.duration_hours)
      }, { headers, withCredentials: true });
      toast.success("Capacitación creada");
      setTrainingDialogOpen(false);
      setTrainingForm({ title: "", description: "", duration_hours: "", instructor: "", category: "" });
      fetchData();
    } catch (error) {
      toast.error("Error al crear capacitación");
    }
  };

  const handleAssignTraining = async (e) => {
    e.preventDefault();
    if (!assignForm.training_id || !assignForm.employee_id) {
      toast.error("Selecciona capacitación y empleado");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(
        `${API}/training-assignments?training_id=${assignForm.training_id}&employee_id=${assignForm.employee_id}&due_date=${assignForm.due_date}`,
        {},
        { headers, withCredentials: true }
      );
      toast.success("Capacitación asignada");
      setAssignDialogOpen(false);
      setAssignForm({ training_id: "", employee_id: "", due_date: "" });
      fetchData();
    } catch (error) {
      toast.error("Error al asignar capacitación");
    }
  };

  const handleUpdateProgress = async (assignmentId, progress) => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(
        `${API}/training-assignments/${assignmentId}/progress?progress=${progress}`,
        {},
        { headers, withCredentials: true }
      );
      toast.success("Progreso actualizado");
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar progreso");
    }
  };

  const getEmployeeName = (employeeId) => {
    const emp = employees.find(e => e.employee_id === employeeId);
    return emp ? `${emp.first_name} ${emp.last_name}` : employeeId;
  };

  const getTrainingTitle = (trainingId) => {
    const training = trainings.find(t => t.training_id === trainingId);
    return training?.title || trainingId;
  };

  const getCategoryColor = (category) => {
    const colors = {
      "Técnico": "bg-blue-100 text-blue-600",
      "Liderazgo": "bg-violet-100 text-violet-600",
      "Comunicación": "bg-emerald-100 text-emerald-600",
      "Seguridad": "bg-red-100 text-red-600",
      "Compliance": "bg-amber-100 text-amber-600",
      "Onboarding": "bg-cyan-100 text-cyan-600"
    };
    return colors[category] || "bg-slate-100 text-slate-600";
  };

  const totalAssigned = assignments.filter(a => a.status === "assigned").length;
  const totalCompleted = assignments.filter(a => a.status === "completed").length;

  return (
    <Layout 
      title="Capacitaciones" 
      subtitle="Gestión de cursos y desarrollo"
      actions={
        <div className="flex gap-2">
          <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="assign-training-btn">
                <Users className="w-4 h-4 mr-2" />
                Asignar
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Asignar Capacitación</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAssignTraining} className="space-y-4">
                <div className="space-y-2">
                  <Label>Capacitación *</Label>
                  <Select value={assignForm.training_id} onValueChange={(v) => setAssignForm(p => ({ ...p, training_id: v }))}>
                    <SelectTrigger data-testid="assign-training-select">
                      <SelectValue placeholder="Selecciona capacitación" />
                    </SelectTrigger>
                    <SelectContent>
                      {trainings.map(t => (
                        <SelectItem key={t.training_id} value={t.training_id}>{t.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Empleado *</Label>
                  <Select value={assignForm.employee_id} onValueChange={(v) => setAssignForm(p => ({ ...p, employee_id: v }))}>
                    <SelectTrigger data-testid="assign-employee-select">
                      <SelectValue placeholder="Selecciona empleado" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map(e => (
                        <SelectItem key={e.employee_id} value={e.employee_id}>
                          {e.first_name} {e.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Fecha Límite</Label>
                  <Input
                    type="date"
                    value={assignForm.due_date}
                    onChange={(e) => setAssignForm(p => ({ ...p, due_date: e.target.value }))}
                    data-testid="assign-due-date-input"
                  />
                </div>
                <DialogFooter>
                  <Button type="submit" className="bg-orange-600 hover:bg-orange-700" data-testid="submit-assign-btn">
                    Asignar
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          
          <Dialog open={trainingDialogOpen} onOpenChange={setTrainingDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-orange-600 hover:bg-orange-700" data-testid="create-training-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nueva Capacitación
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Crear Capacitación</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateTraining} className="space-y-4">
                <div className="space-y-2">
                  <Label>Título *</Label>
                  <Input
                    value={trainingForm.title}
                    onChange={(e) => setTrainingForm(p => ({ ...p, title: e.target.value }))}
                    placeholder="Introducción a React"
                    data-testid="training-title-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Duración (horas) *</Label>
                    <Input
                      type="number"
                      value={trainingForm.duration_hours}
                      onChange={(e) => setTrainingForm(p => ({ ...p, duration_hours: e.target.value }))}
                      placeholder="8"
                      data-testid="training-duration-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Categoría *</Label>
                    <Select value={trainingForm.category} onValueChange={(v) => setTrainingForm(p => ({ ...p, category: v }))}>
                      <SelectTrigger data-testid="training-category-select">
                        <SelectValue placeholder="Selecciona" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Instructor</Label>
                  <Input
                    value={trainingForm.instructor}
                    onChange={(e) => setTrainingForm(p => ({ ...p, instructor: e.target.value }))}
                    placeholder="Nombre del instructor"
                    data-testid="training-instructor-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Descripción</Label>
                  <Textarea
                    value={trainingForm.description}
                    onChange={(e) => setTrainingForm(p => ({ ...p, description: e.target.value }))}
                    placeholder="Descripción del curso..."
                    rows={3}
                    data-testid="training-description-input"
                  />
                </div>
                <DialogFooter>
                  <Button type="submit" className="bg-orange-600 hover:bg-orange-700" data-testid="submit-training-btn">
                    Crear Capacitación
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      }
    >
      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{trainings.length}</p>
                <p className="text-sm text-slate-500">Cursos Disponibles</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{totalAssigned}</p>
                <p className="text-sm text-slate-500">En Progreso</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Award className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{totalCompleted}</p>
                <p className="text-sm text-slate-500">Completados</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {trainings.reduce((acc, t) => acc + t.duration_hours, 0)}h
                </p>
                <p className="text-sm text-slate-500">Horas Totales</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Trainings */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-orange-600" />
              Cursos Disponibles
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : trainings.length === 0 ? (
              <div className="text-center text-slate-500 py-8">
                <GraduationCap className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No hay capacitaciones</p>
              </div>
            ) : (
              <div className="space-y-3">
                {trainings.map((training) => (
                  <div 
                    key={training.training_id} 
                    className="p-4 rounded-lg bg-slate-50 border border-slate-100 hover:border-slate-200 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-slate-900">{training.title}</h4>
                      <Badge className={getCategoryColor(training.category)}>
                        {training.category}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-600 mb-2">{training.description}</p>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {training.duration_hours}h
                      </span>
                      {training.instructor && (
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {training.instructor}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Assignments */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Asignaciones
            </CardTitle>
          </CardHeader>
          <CardContent>
            {assignments.length === 0 ? (
              <div className="text-center text-slate-500 py-8">
                <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No hay asignaciones</p>
              </div>
            ) : (
              <div className="space-y-3">
                {assignments.map((assignment) => (
                  <div 
                    key={assignment.assignment_id} 
                    className="p-4 rounded-lg bg-slate-50 border border-slate-100"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium text-slate-900">{getEmployeeName(assignment.employee_id)}</p>
                        <p className="text-sm text-slate-500">{getTrainingTitle(assignment.training_id)}</p>
                      </div>
                      <Badge className={assignment.status === "completed" ? "badge-success" : "badge-info"}>
                        {assignment.status === "completed" ? "Completado" : "En Progreso"}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Progreso</span>
                        <span className="font-medium">{assignment.progress}%</span>
                      </div>
                      <Progress value={assignment.progress} className="h-2" />
                      {assignment.status !== "completed" && (
                        <div className="flex gap-2 mt-3">
                          {[25, 50, 75, 100].map((p) => (
                            <Button
                              key={p}
                              size="sm"
                              variant={assignment.progress >= p ? "default" : "outline"}
                              className={assignment.progress >= p ? "bg-orange-600" : ""}
                              onClick={() => handleUpdateProgress(assignment.assignment_id, p)}
                            >
                              {p}%
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Training;
